import { generatePath, useNavigate, useParams } from "react-router-dom";

import {
  CenteredBox,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { DangerButton } from "@components/buttons/DangerButton";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { SectionTitle } from "@components/SectionTitle";
import { Item } from "@components/SelectionList";
import { withPermissions } from "@components/withPermissions";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  useClearDeploymentRingConfigurationCache,
  useDeploymentRings
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { RingList } from "./RingList";
import { DevicesAssigned } from "./rollout/devices-assigned/DevicesAssigned";
import { RolloutList } from "./rollout/RolloutList";
import { DeploymentRingSelectorHost } from "./selector";

const CLEAR_DEPLOYMENT_CACHE_TOOLTIP =
  "Clears the deployment ring block distributed cache for this deployment ring.";

const DeployRingsBase: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { ringId } = useParams<{ ringId: string }>();

  const selectRing = (item: Item) => {
    const to = generatePath("/system/plt/deployment-rings/:ringId", {
      ringId: item.id
    });
    navigate(to);
  };

  const clearDeviceFieldConfigurationCache = useClearDeploymentRingConfigurationCache(
    ringId!
  );

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const deploymentRingsQuery = useDeploymentRings();
  return (
    <>
      <SectionTitle>Deployment Rings</SectionTitle>
      <QueryStateIndicator {...deploymentRingsQuery}>
        {rings => {
          const ring = rings.find(x => x.id === ringId);

          return (
            <Stack
              horizontal
              verticalFill
              tokens={{ childrenGap: theme.spacing.m }}
            >
              <Stack styles={{ root: { width: "300px" } }}>
                <RingList
                  deploymentRings={rings}
                  deploymentRing={ring}
                  onSelectedItemChange={selectRing}
                />
              </Stack>
              <Stack verticalFill styles={{ root: { width: "100%" } }}>
                {ring ? (
                  <Stack
                    verticalFill
                    tokens={{ childrenGap: theme.spacing.s1 }}
                  >
                    <Stack
                      horizontal
                      styles={{ root: { justifyContent: "space-between" } }}
                    >
                      <Stack>
                        <Text variant="xLarge">
                          {ring.displayName} - {ring.code}
                        </Text>
                        <Text>{ring.description}</Text>
                      </Stack>

                      <TooltipHost
                        content={CLEAR_DEPLOYMENT_CACHE_TOOLTIP}
                        styles={{ root: { minWidth: 290 } }}
                      >
                        <DangerButton
                          disabled={!hasWritePermission}
                          title={
                            !hasWritePermission
                              ? NO_ACTION_PERMISSION
                              : undefined
                          }
                          onClick={clearDeviceFieldConfigurationCache}
                        >
                          Clear Cached Deployment Block
                        </DangerButton>
                      </TooltipHost>
                    </Stack>

                    <Stack
                      tokens={{ childrenGap: theme.spacing.m }}
                      styles={{
                        root: { overflow: "auto", height: "100%" }
                      }}
                    >
                      <DeploymentRingSelectorHost deploymentRing={ring} />

                      <Stack
                        horizontal
                        tokens={{ childrenGap: theme.spacing.m }}
                      >
                        <RolloutList deploymentRingId={ring.id} />
                        <DevicesAssigned deploymentRingId={ring.id} />
                      </Stack>
                    </Stack>
                  </Stack>
                ) : (
                  <CenteredBox>
                    <Text>Please select a deployment ring</Text>
                  </CenteredBox>
                )}
              </Stack>
            </Stack>
          );
        }}
      </QueryStateIndicator>
    </>
  );
};

export const DeployRings = withPermissions(
  DeployRingsBase,
  [Permissions.PltFieldManagementRead, Permissions.PltFieldManagementWrite],
  "or"
);
